import { z } from "zod";
import { IdSchema, IsoDateTimeSchema } from "./common.js";

export const RuntimeStatusSchema = z.enum([
	"CREATING",
	"RUNNING",
	"STOPPED",
	"DESTROYED",
	"FAILED",
]);
export type RuntimeStatus = z.infer<typeof RuntimeStatusSchema>;

export const RuntimePurposeSchema = z.enum(["PROJECT", "CI", "DEPLOY"]);
export type RuntimePurpose = z.infer<typeof RuntimePurposeSchema>;

export const RuntimeSchema = z.object({
	id: IdSchema,
	tenantId: IdSchema,
	projectId: IdSchema,
	provider: z.enum(["LOCAL", "FLY"]),
	purpose: RuntimePurposeSchema.default("PROJECT"),
	status: RuntimeStatusSchema,
	imageTag: z.string(),
	createdAt: IsoDateTimeSchema,
	startedAt: IsoDateTimeSchema.nullable(),
	stoppedAt: IsoDateTimeSchema.nullable(),
	runtimeSeconds: z.number().min(0),
});
export type Runtime = z.infer<typeof RuntimeSchema>;

/** Internal watchdog contract. Deliberately contains no Runtime/provider identifiers. */
export const ProjectRuntimeHealthSchema = z.object({
	status: z.enum(["HEALTHY", "UNHEALTHY", "MISSING"]),
	observedAt: IsoDateTimeSchema,
});
export type ProjectRuntimeHealth = z.infer<typeof ProjectRuntimeHealthSchema>;

export const CreateRuntimeRequestSchema = z.object({
	projectId: IdSchema,
	provider: z.enum(["LOCAL", "FLY"]).default("LOCAL"),
});
export type CreateRuntimeRequest = z.infer<typeof CreateRuntimeRequestSchema>;

export const PrepareRuntimeWorkspaceRequestSchema = z.object({
	cloneUrl: z.string().url(),
	username: z.string().min(1),
	credential: z.string().min(1),
	ref: z.string().min(1).optional(),
});
export type PrepareRuntimeWorkspaceRequest = z.infer<typeof PrepareRuntimeWorkspaceRequestSchema>;

export const PrepareRuntimeWorkspaceResponseSchema = z.object({
	runtimeId: IdSchema,
	operation: z.enum(["CLONED", "PULLED"]),
});
export type PrepareRuntimeWorkspaceResponse = z.infer<typeof PrepareRuntimeWorkspaceResponseSchema>;

export const WorkspaceDiffSummaryEntrySchema = z.object({
	path: z.string().min(1),
	additions: z.number().int().min(0),
	deletions: z.number().int().min(0),
});
export type WorkspaceDiffSummaryEntry = z.infer<typeof WorkspaceDiffSummaryEntrySchema>;

export const FinalizeRuntimeWorkspaceRequestSchema = z.object({
	branch: z.string().min(1).max(255),
	message: z.string().min(1).max(4096),
	expectedBase: z.string().min(1).max(255),
});
export type FinalizeRuntimeWorkspaceRequest = z.infer<typeof FinalizeRuntimeWorkspaceRequestSchema>;

export const FinalizeRuntimeWorkspaceResponseSchema = z.object({
	commitSha: z.string().regex(/^[0-9a-f]{40}$/),
	diffSummary: z.array(WorkspaceDiffSummaryEntrySchema),
});
export type FinalizeRuntimeWorkspaceResponse = z.infer<
	typeof FinalizeRuntimeWorkspaceResponseSchema
>;

export const CreateAutomationRuntimeRequestSchema = z.object({
	tenantId: IdSchema,
	projectId: IdSchema,
	purpose: z.enum(["CI", "DEPLOY"]),
	secretEnvironment: z.record(z.string(), z.string().min(1)).default({}),
});

export const EnsureJourneyRuntimeRequestSchema = z.object({
	tenantId: IdSchema,
	projectId: IdSchema,
});
export type EnsureJourneyRuntimeRequest = z.infer<typeof EnsureJourneyRuntimeRequestSchema>;

/** 並行実行のために Project の PROJECT Runtime を maxCount 台まで増やす（内部用、platform_admin） */
export const EnsureRuntimeCapacityRequestSchema = z.object({
	tenantId: IdSchema,
	projectId: IdSchema,
	maxCount: z.number().int().min(1).max(8),
});
export type EnsureRuntimeCapacityRequest = z.infer<typeof EnsureRuntimeCapacityRequestSchema>;

export const EnsureRuntimeCapacityResponseSchema = z.object({
	/** 新しく作った Runtime。上限に達していれば null */
	runtime: RuntimeSchema.nullable(),
	count: z.number().int().min(0),
});
export type EnsureRuntimeCapacityResponse = z.infer<typeof EnsureRuntimeCapacityResponseSchema>;

export const AutomationRuntimeCommandRequestSchema = z.object({
	tenantId: IdSchema,
	file: z.string().min(1),
	args: z.array(z.string()),
	timeoutMs: z.number().int().min(1000).max(2_700_000),
});

export const PrepareAutomationWorkspaceRequestSchema = PrepareRuntimeWorkspaceRequestSchema.extend({
	tenantId: IdSchema,
});

export const DestroyAutomationRuntimeRequestSchema = z.object({ tenantId: IdSchema });

export const AutomationRuntimeCommandResponseSchema = z.object({
	exitCode: z.number().int().nullable(),
	canceled: z.boolean(),
	timedOut: z.boolean(),
	outputTail: z.string().max(8192),
});

export const WorkOrderRuntimeCommandFrameSchema = z.object({
	sequence: z.number().int().min(0),
	event: z.enum(["exec", "stdout", "stderr", "error", "exit", "truncated"]),
	data: z.unknown(),
	createdAt: IsoDateTimeSchema,
});
export const WorkOrderRuntimeCommandResponseSchema = AutomationRuntimeCommandResponseSchema.extend({
	execId: IdSchema,
	frames: z.array(WorkOrderRuntimeCommandFrameSchema),
});
export type WorkOrderRuntimeCommandResponse = z.infer<typeof WorkOrderRuntimeCommandResponseSchema>;
export const WorkOrderRuntimeFramesSnapshotSchema = z.object({
	items: z.array(WorkOrderRuntimeCommandFrameSchema),
	lastSequence: z.number().int().min(-1),
	complete: z.boolean(),
});
export type WorkOrderRuntimeFramesSnapshot = z.infer<typeof WorkOrderRuntimeFramesSnapshotSchema>;

export const ExecRequestSchema = z.object({
	command: z.string().min(1).max(8000),
	timeoutMs: z.number().int().min(1000).max(600000).default(120000),
});
export type ExecRequest = z.infer<typeof ExecRequestSchema>;

export const DevChatCliSchema = z.enum(["claude", "codex", "shell"]);
export type DevChatCli = z.infer<typeof DevChatCliSchema>;

export const CliKindSchema = z.enum(["claude", "codex"]);
export type CliKind = z.infer<typeof CliKindSchema>;

export const CliAuthStatusSchema = z.object({
	cli: CliKindSchema,
	authenticated: z.boolean(),
	method: z.enum(["SUBSCRIPTION", "API_KEY", "NONE"]),
	hostShared: z.boolean().optional(),
	checkedAt: IsoDateTimeSchema,
});
export type CliAuthStatus = z.infer<typeof CliAuthStatusSchema>;

export const RuntimeAuthStatusResponseSchema = z.object({
	runtimeId: IdSchema,
	entries: z.array(CliAuthStatusSchema),
});
export type RuntimeAuthStatusResponse = z.infer<typeof RuntimeAuthStatusResponseSchema>;

export const ExecInputRequestSchema = z.object({
	data: z.string().min(1).max(4000),
	appendNewline: z.boolean().default(true),
});
export type ExecInputRequest = z.infer<typeof ExecInputRequestSchema>;

export const RuntimeExecStatusSchema = z.enum(["RUNNING", "EXITED", "FAILED", "CANCELED"]);
export const RuntimeExecKindSchema = z.enum(["EXEC", "AUTH"]);
export const RuntimeExecSummarySchema = z.object({
	id: IdSchema,
	runtimeId: IdSchema,
	status: RuntimeExecStatusSchema,
	kind: RuntimeExecKindSchema,
	cli: DevChatCliSchema,
	startedAt: IsoDateTimeSchema,
});
export type RuntimeExecSummary = z.infer<typeof RuntimeExecSummarySchema>;
export const RuntimeExecListResponseSchema = z.object({
	items: z.array(RuntimeExecSummarySchema),
});
export type RuntimeExecListResponse = z.infer<typeof RuntimeExecListResponseSchema>;

export const DevChatExecRequestSchema = z.object({
	cli: DevChatCliSchema,
	prompt: z.string().min(1).max(32000),
	sessionId: IdSchema.optional(),
	timeoutMs: z.number().int().min(1000).max(1800000).default(600000),
});
export type DevChatExecRequest = z.infer<typeof DevChatExecRequestSchema>;
